import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { SectionCardComponent } from '../section-card/section-card';
import { cloneDeep } from '../../utils/clone';

type DimensionsMapValue = Record<string, { width: number; height: number }>;

const DIM_PRESETS: DimensionsMapValue = {
	low: { width: 640, height: 360 },
	medium: { width: 1280, height: 720 },
};

@Component({
	selector: 'app-dimensions-editor',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, InputNumber, SectionCardComponent],
	template: `
		<app-section-card [title]="title()">
			<div class="px-3 py-3 flex flex-col gap-2">
				@for (key of dimKeys; track key) {
					<div class="flex items-end gap-3">
						<span class="text-wb-secondary text-[11px] font-semibold uppercase tracking-wider w-14 shrink-0 pb-[5px]">{{ dimLabels[key] }}</span>
						<div class="form-field">
							<label class="form-label">W</label>
							<p-inputnumber [ngModel]="getDim(key, 'width')" (ngModelChange)="setDim(key, 'width', $event)"
								[showButtons]="false" styleClass="p-inputnumber-fluid" />
						</div>
						<span class="text-wb-muted text-[13px] pb-[5px]">&times;</span>
						<div class="form-field">
							<label class="form-label">H</label>
							<p-inputnumber [ngModel]="getDim(key, 'height')" (ngModelChange)="setDim(key, 'height', $event)"
								[showButtons]="false" styleClass="p-inputnumber-fluid" />
						</div>
					</div>
				}
			</div>
		</app-section-card>
	`,
})
export class DimensionsEditorComponent {
	title = input('Dimensions Map');
	dimensionsMap = input<DimensionsMapValue>({});
	dimensionsMapChange = output<DimensionsMapValue>();

	protected readonly dimKeys = ['low', 'medium'] as const;
	protected readonly dimLabels: Record<string, string> = { low: 'Low', medium: 'Medium' };

	protected getDim(key: string, axis: 'width' | 'height'): number {
		const map = this.dimensionsMap() ?? {};
		return map[key]?.[axis] ?? DIM_PRESETS[key]?.[axis] ?? 0;
	}

	protected setDim(key: string, axis: 'width' | 'height', val: number | null): void {
		const map = cloneDeep(this.dimensionsMap() ?? {});
		if (!map[key]) map[key] = { ...DIM_PRESETS[key] };
		map[key][axis] = val ?? 0;
		this.dimensionsMapChange.emit(map);
	}
}
